// The "Included crews" checkboxes' state, extracted from the fieldset so the
// only arithmetic in the feature can be tested without rendering anything —
// the same split, and the same reason, as partner-locations-state.ts.
//
// The crew list has no grouping (crews do not nest the way cities sit in
// countries), so there is no "all in X" three-state box to get wrong. What it
// has instead is a SEARCH box over a list that can run to hundreds of rows,
// and a per-crew WARNING badge whose absence is the dangerous case: a Model
// 2/3 partner silently does not get served a crew ops ticked, because that
// crew has agreed no supplier-commission rate. The filter must never hide a
// TICKED crew — a crew you cannot see is a crew you cannot untick, and Save
// sends the selection, not the visible rows.
//
// The API (PUT /admin/partners/:id/vendors → setPartnerVendors) stays the
// authority: it is a replace-set and it refuses the house crew, the Avis
// supplier and unknown ids. This is the guard rail in front of it.

/** An active crew the picker may offer. */
export interface IncludableCrew {
  id: number;
  name: string;
}

/** Why an included crew cannot actually be served to this partner. */
export interface CrewWarning {
  vendorId: number;
  name: string;
  reason: 'no_supplier_rate';
}

/** On ⇄ off for one crew. Deduplicates on the way in: an id that somehow
 *  arrived twice must not need two clicks to come off. */
export function toggleCrew(selected: number[], vendorId: number): number[] {
  if (selected.includes(vendorId)) return selected.filter((id) => id !== vendorId);
  return [...selected, vendorId];
}

/**
 * The crews the search box leaves on screen: a case-insensitive substring
 * match on the name, PLUS every crew that is currently ticked whatever the
 * query says.
 *
 * That second half is not a convenience. Save sends `selected`, not what is
 * rendered — so a ticked crew filtered out of view is one the admin cannot
 * untick and cannot see they are about to keep. Typing a query would then
 * look like it narrowed the partner's crews while actually changing nothing.
 */
export function filterCrews(
  crews: IncludableCrew[],
  selected: number[],
  query: string,
): IncludableCrew[] {
  const q = query.trim().toLowerCase();
  if (q === '') return crews;
  const on = new Set(selected);
  return crews.filter((c) => on.has(c.id) || c.name.toLowerCase().includes(q));
}

/**
 * "N crews included" for the summary line. Counts DISTINCT ids, including one
 * the picker no longer knows about (a crew suspended since the page loaded) —
 * the partner is still configured to sell it, and hiding that is how the
 * number stops matching what Save will send.
 */
export function inclusionCount(selected: number[]): number {
  return new Set(selected).size;
}

/**
 * The warnings that belong on rows the admin can actually see and act on,
 * keyed by crew id.
 *
 * Scoped to the SELECTED crews on purpose: the server also reports warnings
 * for an `all_vendors` partner (every unrated active crew), and rendering a
 * badge next to an unticked crew would read as "this crew is broken" rather
 * than "the crew you included will not be served".
 */
export function warningsBySelectedCrew(
  warnings: CrewWarning[],
  selected: number[],
): Map<number, CrewWarning> {
  const on = new Set(selected);
  const out = new Map<number, CrewWarning>();
  for (const w of warnings) if (on.has(w.vendorId)) out.set(w.vendorId, w);
  return out;
}
